import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, isAuthenticated, initializeSystem } from '../utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Wallet, Lock, Phone, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export const Login = () => {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPinPad, setShowPinPad] = useState(false);
  const [initInfo, setInitInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already authenticated
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
    
    // Try to initialize system (will return info if first time)
    initializeSystem().then(data => {
      if (data.initialized) {
        setInitInfo(data.credentials);
      }
    }).catch(() => {
      // System already initialized or error - that's fine
    });
  }, [navigate]);

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    if (phone.length >= 10) {
      setShowPinPad(true);
    } else {
      toast.error('Please enter a valid phone number');
    }
  };

  const handlePinInput = async (digit) => {
    if (pin.length < 4 && !loading) {
      const newPin = pin + digit;
      setPin(newPin);
      
      if (newPin.length === 4) {
        setLoading(true);
        try {
          const data = await login(phone, newPin);
          toast.success(`Welcome, ${data.user.name}!`);
          navigate('/dashboard');
        } catch (error) {
          toast.error(error.message || 'Invalid credentials');
          setPin('');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
  };

  const handleBack = () => {
    setShowPinPad(false);
    setPin('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <Wallet className="w-16 h-16 text-emerald-900" />
              <Building2 className="w-6 h-6 text-emerald-700 absolute -bottom-1 -right-1 bg-stone-50 rounded-full p-0.5" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-emerald-900 tracking-tight mb-2">FinanceGarden</h1>
          <p className="text-stone-600">Multi-Company Finance Tracker</p>
        </div>

        {initInfo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4"
          >
            <p className="text-sm font-medium text-emerald-900 mb-2">System initialized! Default Super Admin credentials:</p>
            <div className="text-sm text-emerald-700 space-y-1">
              <p><span className="font-medium">Phone:</span> {initInfo.phone}</p>
              <p><span className="font-medium">PIN:</span> {initInfo.pin}</p>
            </div>
          </motion.div>
        )}

        <div className="bg-white border border-stone-200 rounded-xl p-8">
          {!showPinPad ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-stone-700 font-medium">
                  Phone Number
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    className="pl-10 h-12 text-lg"
                    data-testid="login-phone-input"
                    autoFocus
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 bg-emerald-900 hover:bg-emerald-800 text-white text-lg font-medium rounded-xl"
                data-testid="login-continue-btn"
              >
                Continue
              </Button>
            </form>
          ) : (
            <>
              <button
                onClick={handleBack}
                className="text-sm text-stone-500 hover:text-stone-700 mb-4 flex items-center gap-1"
                data-testid="login-back-btn"
              >
                ← Back
              </button>
              
              <p className="text-center text-stone-600 mb-4">
                Enter PIN for <span className="font-medium text-emerald-900">{phone}</span>
              </p>

              {/* PIN Display */}
              <div className="flex justify-center gap-4 mb-8">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center ${
                      pin.length > i
                        ? 'border-emerald-900 bg-emerald-50'
                        : 'border-stone-200 bg-stone-50'
                    }`}
                  >
                    {pin.length > i && (
                      <Lock className="w-5 h-5 text-emerald-900" />
                    )}
                  </div>
                ))}
              </div>

              {/* Number Pad */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <Button
                    key={digit}
                    onClick={() => handlePinInput(digit.toString())}
                    disabled={loading}
                    className="h-14 text-xl font-semibold bg-stone-100 hover:bg-emerald-50 hover:text-emerald-900 text-stone-900 rounded-xl"
                    data-testid={`pin-${digit}`}
                  >
                    {digit}
                  </Button>
                ))}
                <Button
                  onClick={handleClear}
                  disabled={loading}
                  className="h-14 bg-stone-100 hover:bg-orange-50 hover:text-orange-800 text-stone-600 rounded-xl text-sm"
                  data-testid="pin-clear"
                >
                  Clear
                </Button>
                <Button
                  onClick={() => handlePinInput('0')}
                  disabled={loading}
                  className="h-14 text-xl font-semibold bg-stone-100 hover:bg-emerald-50 hover:text-emerald-900 text-stone-900 rounded-xl"
                  data-testid="pin-0"
                >
                  0
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={loading}
                  className="h-14 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-xl text-sm"
                  data-testid="pin-delete"
                >
                  Delete
                </Button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-sm text-stone-500 mt-6">
          Secure multi-company finance management
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
